import { useEffect } from 'react';
import type Konva from 'konva';

export function useCanvasStageResize(input: {
  stageRef: { current: Konva.Stage | null };
  setDimensions: (next: { width: number; height: number }) => void;
}): void {
  const { stageRef, setDimensions } = input;

  useEffect(() => {
    let cancelled = false;
    let resizeObserver: ResizeObserver | null = null;
    let observedContainer: HTMLElement | null = null;

    const updateDimensions = () => {
      if (!observedContainer || cancelled) return;
      const width = observedContainer.clientWidth;
      const height = observedContainer.clientHeight;
      // A transiently collapsed container (hidden tab, embedded pane mid-
      // layout) reports 0×0; sizing the Konva stage to zero makes its buffer
      // canvas 0-sized and any shadowed shape then crashes drawImage during
      // Stage._resizeDOM. Keep the previous dimensions until the container
      // has real extent again.
      if (width <= 0 || height <= 0) return;
      setDimensions({ width, height });
    };

    const attach = (): boolean => {
      const container = stageRef.current?.container().parentElement;
      if (!container || cancelled) return false;

      observedContainer = container;
      updateDimensions();

      resizeObserver = new ResizeObserver(() => {
        updateDimensions();
      });
      resizeObserver.observe(container);
      window.addEventListener('resize', updateDimensions);
      return true;
    };

    const detach = () => {
      resizeObserver?.disconnect();
      resizeObserver = null;
      if (observedContainer) {
        window.removeEventListener('resize', updateDimensions);
        observedContainer = null;
      }
    };

    if (!attach()) {
      const frameId = requestAnimationFrame(() => {
        if (!cancelled && !observedContainer) attach();
      });
      return () => {
        cancelled = true;
        cancelAnimationFrame(frameId);
        detach();
      };
    }

    return () => {
      cancelled = true;
      detach();
    };
  }, [setDimensions, stageRef]);
}
